"use client";

import { useEffect, useRef } from "react";

// Cloudflare's documented "always passes" test sitekey.
const TEST_SITEKEY = "1x00000000000000000000AA";

/**
 * Fall back to the always-passes test key in development only.
 *
 * Falling back unconditionally meant a missing NEXT_PUBLIC_TURNSTILE_SITEKEY at
 * build time silently shipped a bot check that passes everything, with nothing
 * in the output to say so. Registration now depends on this, so a production
 * build without the key fails loudly at build time instead.
 */
function resolveSitekey(): string {
  const configured = process.env.NEXT_PUBLIC_TURNSTILE_SITEKEY;
  if (configured) return configured;
  if (process.env.NODE_ENV === "production") {
    throw new Error(
      "NEXT_PUBLIC_TURNSTILE_SITEKEY is not set. Refusing to build a production bundle " +
        "with Cloudflare's always-passes test sitekey.",
    );
  }
  return TEST_SITEKEY;
}

const SITEKEY = resolveSitekey();

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
    };
  }
}

let scriptLoading: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
  return scriptLoading;
}

export function Turnstile({ onVerify }: { onVerify: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !ref.current || !window.turnstile) return;
      window.turnstile.render(ref.current, {
        sitekey: SITEKEY,
        theme: "dark",
        callback: (token: string) => onVerify(token),
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} className="min-h-[65px]" />;
}
